import { generateId } from '@/utils/format'
import { getStatusCounts, getContainerList, getInTransitValue } from '@/api/dashboard'
import { toBrtISOString, toBrtDate, toBrtDateTime, brtMoment } from '@/utils/timezone'

function mapApiStatusToLocal(apiStatus) {
  const validStatuses = ['shipping', 'arrived', 'pending_delivery', 'warehouse', 'completed']
  // 如果已经是合法的字符串状态，直接透传
  if (validStatuses.includes(apiStatus)) {
    return apiStatus
  }
  const statusMap = {
    0: 'shipping',
    1: 'shipping',
    2: 'arrived',
    3: 'pending_delivery',
    4: 'warehouse',
    5: 'completed'
  }
  // 兼容字符串数字（如 '0', '1'）和数字类型
  const key = typeof apiStatus === 'string' && !isNaN(Number(apiStatus))
    ? Number(apiStatus)
    : apiStatus
  return statusMap[key] || 'shipping'
}

const state = () => ({
  shipments: [],
  statusCounts: null,
  totalInTransitValue: null
})

const getters = {
  allShipments: state => state.shipments,

  shipmentsByStatus: state => status => state.shipments.filter(s => s.status === status),

  inTransitShipments: state => state.shipments.filter(s => !s.erpStockedIn),

  totalInTransitValue: state => {
    if (state.totalInTransitValue !== null) {
      return state.totalInTransitValue
    }
    return 0
  },

  inTransitCount: (state, getters) => getters.inTransitShipments.length,

  monthlyForecast: state => (year, month) => {
    const prefix = `${year}-${String(month).padStart(2, '0')}`
    return state.shipments.filter(s => {
      return s.eta && s.eta.startsWith(prefix) && !s.ata
    })
  },

  stats: state => {
    if (state.statusCounts) {
      return {
        total: state.statusCounts.status1 || 0,
        shipping: state.statusCounts.status2 || 0,
        arrived: state.statusCounts.status3 || 0,
        pending_delivery: state.statusCounts.status4 || 0,
        warehouse: state.statusCounts.status5 || 0,
        completed: state.statusCounts.status6 || 0
      }
    }
    return {
      total: 0,
      shipping: 0,
      arrived: 0,
      pending_delivery: 0,
      warehouse: 0,
      completed: 0
    }
  }
}

const mutations = {
  SET_STATUS_COUNTS(state, counts) {
    state.statusCounts = {
      status1: counts.status1 || 0,
      status2: counts.status2 || 0,
      status3: counts.status3 || 0,
      status4: counts.status4 || 0,
      status5: counts.status5 || 0,
      status6: counts.status6 || 0
    }
  },

  SET_IN_TRANSIT_VALUE(state, value) {
    state.totalInTransitValue = value
  },

  SET_CONTAINER_LIST(state, containers) {
    state.shipments = containers.map(c => ({
      id: c.id,
      rawCode: c.rawCode,
      loadingDate: c.loadingDate,
      batchNo: c.batchNo,
      departurePort: c.departurePort,
      containerNo: c.containerNo,
      internalCabinetNo: c.internalCabinetNo,
      receivingCompany: c.receivingCompany,
      totalValue: c.totalValue,
      eta: c.eta,
      ata: c.ata,
      etd: c.etd,
      status: mapApiStatusToLocal(c.status),
      statusName: c.statusName,
      billNo: c.billNo || c.blNo,
      erpStockedIn: c.erpChecked === 1,
      warehouseChecked: c.warehouseChecked === 1,
      customsClearanceTime: null,
      deliveryTime: null,
      hasAbnormal: null,
      abnormalType: '',
      images: [],
      skuItems: [],
      isLocked: c.status === 5,
      history: [],
      createdBy: 'api',
      createdAt: c.createTime,
      updatedAt: c.updateTime
    }))
  },

  ADD_SHIPMENT(state, shipment) {
    state.shipments.unshift(shipment)
  },

  UPDATE_SHIPMENT(state, { id, updates }) {
    const idx = state.shipments.findIndex(s => s.id === id)
    if (idx !== -1) {
      state.shipments.splice(idx, 1, { ...state.shipments[idx], ...updates, updatedAt: toBrtDate(new Date()) })
    }
  },

  DELETE_SHIPMENT(state, id) {
    const idx = state.shipments.findIndex(s => s.id === id)
    if (idx !== -1) {
      state.shipments.splice(idx, 1)
    }
  },

  LOCK_SHIPMENT(state, id) {
    const idx = state.shipments.findIndex(s => s.id === id)
    if (idx !== -1) {
      state.shipments[idx].isLocked = true
    }
  },

  ADD_HISTORY(state, { id, historyEntry }) {
    const idx = state.shipments.findIndex(s => s.id === id)
    if (idx !== -1) {
      if (!state.shipments[idx].history) {
        state.shipments[idx].history = []
      }
      state.shipments[idx].history.push(historyEntry)
    }
  },

  ROLLBACK(state, { id, historyIndex }) {
    const idx = state.shipments.findIndex(s => s.id === id)
    if (idx !== -1) {
      const shipment = state.shipments[idx]
      if (shipment.history && shipment.history[historyIndex] && shipment.history[historyIndex].previousState) {
        const rollbackState = shipment.history[historyIndex].previousState
        Object.assign(state.shipments[idx], rollbackState)
      }
    }
  }
}

const actions = {
  async loadContainerList({ commit }) {
    try {
      const res = await getContainerList({ pageNum: 1, pageSize: 100 })
      if (res && res.rows) {
        commit('SET_CONTAINER_LIST', res.rows)
      }
    } catch (error) {
      console.error('加载集装箱列表失败:', error)
    }
  },

  async loadDashboardData({ commit }) {
    try {
      const [statusCountsRes, inTransitValueRes, containerListRes] = await Promise.all([
        getStatusCounts(),
        getInTransitValue(),
        getContainerList({ pageNum: 1, pageSize: 100 })
      ])

      if (statusCountsRes && statusCountsRes.data) {
        commit('SET_STATUS_COUNTS', statusCountsRes.data)
      }

      if (inTransitValueRes && inTransitValueRes.data) {
        commit('SET_IN_TRANSIT_VALUE', inTransitValueRes.data.totalValue)
      }

      if (containerListRes && containerListRes.rows) {
        commit('SET_CONTAINER_LIST', containerListRes.rows)
      }
    } catch (error) {
      console.error('加载仪表盘数据失败:', error)
    }
  },

  async loadMonthlyForecast({ commit }, { year, month }) {
    try {
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`
      const lastDay = brtMoment(`${year}-${String(month).padStart(2, '0')}-01`).endOf('month').date()
      const endDate = `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`

      const res = await getContainerList({
        etaStart: startDate,
        etaEnd: endDate,
        pageNum: 1,
        pageSize: 1000
      })

      const forecastContainers = res.rows ? res.rows.filter(c => !c.ata) : []

      commit('SET_CONTAINER_LIST', forecastContainers)

      return forecastContainers.length
    } catch (error) {
      console.error('加载月度预测数据失败:', error)
      return 0
    }
  },

  addShipment({ commit }, shipment) {
    const newShipment = {
      ...shipment,
      id: generateId(),
      createdAt: toBrtDate(new Date()),
      updatedAt: toBrtDate(new Date()),
      status: 'shipping',
      ata: null,
      customsClearanceTime: null,
      deliveryTime: null,
      erpStockedIn: false,
      warehouseChecked: false,
      hasAbnormal: null,
      abnormalType: '',
      images: [],
      skuItems: shipment.skuItems || [],
      isLocked: false,
      history: [{
        action: 'create',
        timestamp: toBrtDateTime(new Date()),
        operator: '管理员',
        previousState: null
      }]
    }
    commit('ADD_SHIPMENT', newShipment)
    return newShipment
  },

  updateShipment({ commit, state }, { id, updates }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment) {
      const previousState = { ...shipment }
      delete previousState.history
      commit('ADD_HISTORY', {
        id,
        historyEntry: {
          action: 'update',
          timestamp: toBrtDateTime(new Date()),
          operator: '管理员',
          previousState
        }
      })
    }
    commit('UPDATE_SHIPMENT', { id, updates })
  },

  confirmArrival({ commit, state }, { id, ata }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'shipping') {
      commit('UPDATE_SHIPMENT', { id, updates: { ata, status: 'arrived' } })
    }
  },

  confirmCustomsClearance({ commit, state }, { id, customsClearanceTime }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'arrived') {
      commit('UPDATE_SHIPMENT', { id, updates: { customsClearanceTime, status: 'pending_delivery' } })
    }
  },

  confirmDelivery({ commit, state }, { id, deliveryTime }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'pending_delivery') {
      commit('UPDATE_SHIPMENT', { id, updates: { deliveryTime, status: 'warehouse' } })
    }
  },

  confirmErpStockedIn({ commit, state }, id) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'warehouse' && !shipment.erpStockedIn) {
      commit('UPDATE_SHIPMENT', { id, updates: { erpStockedIn: true } })
    }
  },

  confirmWarehouseCheck({ commit, state }, id) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'warehouse') {
      commit('UPDATE_SHIPMENT', { id, updates: { warehouseChecked: true } })
    }
  },

  saveWarehouseStatus({ commit, state, dispatch }, { id, erpStockedIn, warehouseChecked, hasAbnormal, abnormalType, images }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.status === 'warehouse') {
      const updates = {
        erpStockedIn: erpStockedIn,
        warehouseChecked: warehouseChecked
      }

      if (warehouseChecked) {
        updates.hasAbnormal = hasAbnormal
        updates.abnormalType = abnormalType || ''
        updates.images = images || shipment.images
      }

      if (erpStockedIn && warehouseChecked) {
        updates.status = 'completed'
        updates.completedTime = toBrtDateTime(new Date())
      }

      commit('UPDATE_SHIPMENT', { id, updates })

      if (erpStockedIn && warehouseChecked) {
        dispatch('lockShipment', id)
      }
    }
  },

  deleteShipment({ commit }, id) {
    commit('DELETE_SHIPMENT', id)
  },

  lockShipment({ commit }, id) {
    commit('LOCK_SHIPMENT', id)
  },

  addHistory({ commit }, { id, historyEntry }) {
    commit('ADD_HISTORY', { id, historyEntry })
  },

  rollbackToHistory({ commit, state }, { id, historyIndex }) {
    const shipment = state.shipments.find(s => s.id === id)
    if (shipment && shipment.history && shipment.history[historyIndex]) {
      commit('ROLLBACK', { id, historyIndex })
      commit('ADD_HISTORY', {
        id,
        historyEntry: {
          action: 'rollback',
          timestamp: toBrtDateTime(new Date()),
          operator: '管理员',
          previousState: null
        }
      })
    }
  }
}

export default { namespaced: true, state, getters, mutations, actions }
